import { useAppSelector } from "../../store/hooks";
import { useCancelPreorderMutation, useGetMyPreordersQuery, useSavePreorderMutation } from "./printsApi";
import type { PrintPreorder } from "./printsApi";
import type { PrintEdition, PrintFormat } from "./printProducts";
import { askPromise } from "./promise/promiseStore";
import { preorderAsk, removeAsk } from "./promise/promiseCopy";

// The signed-in member's pre-orders of posters and wall cards, every day, and one way to change
// them: set a quantity. A new pre-order is a promise the member confirms in the modal; raising or
// lowering a quantity needs nothing; going to 0 removes it for good, after a warning. Everything
// that shows or changes pre-orders goes through here (trading cards: useGathering).

export const MAX_QUANTITY = 10;

export interface Preorders {
  signedIn: boolean;
  /** The member's pre-orders have loaded: until then a button can't tell a new pre-order from one held. */
  ready: boolean;
  list: PrintPreorder[];
  held: (number: number, format: PrintFormat, edition: PrintEdition) => PrintPreorder | undefined;
  /** Removed for good: it can never be pre-ordered (or gathered) again. */
  locked: (number: number, format: PrintFormat, edition: PrintEdition) => boolean;
  /** Resolves true when the change was made, false when the member said no in the modal. */
  setQuantity: (number: number, format: PrintFormat, edition: PrintEdition, quantity: number) => Promise<boolean>;
  busy: boolean;
  error: unknown;
}

export function useSignedIn(): boolean {
  const auth = useAppSelector((s) => s.auth);
  return auth.bootstrapStatus === "ready" && Boolean(auth.token);
}

export function usePreorders(): Preorders {
  const signedIn = useSignedIn();
  const { data } = useGetMyPreordersQuery(undefined, { skip: !signedIn });
  const [save, saving] = useSavePreorderMutation();
  const [cancel, cancelling] = useCancelPreorderMutation();

  const list = data?.preorders ?? [];
  const held = (n: number, f: PrintFormat, e: PrintEdition) =>
    list.find((p) => p.number === n && p.format === f && p.edition === e);
  const locked = (n: number, f: PrintFormat, e: PrintEdition) =>
    (data?.locked ?? []).some((l) => l.number === n && l.format === f && l.edition === e);

  return {
    signedIn,
    ready: data !== undefined,
    list,
    held,
    locked,
    setQuantity: async (n, f, e, q) => {
      const existing = held(n, f, e);
      if (q < 1) {
        if (!existing || !(await askPromise(removeAsk(n, f, e)))) return false;
        await cancel(existing.id);
        return true;
      }
      const quantity = Math.min(q, MAX_QUANTITY);
      if (existing) {
        await save({ number: n, format: f, edition: e, quantity });
        return true;
      }
      if (!(await askPromise(preorderAsk(n, f, e)))) return false;
      const res = await save({ number: n, format: f, edition: e, quantity, confirm: true });
      return !("error" in res);
    },
    busy: saving.isLoading || cancelling.isLoading,
    error: saving.error || cancelling.error,
  };
}

export function preorderErrorText(err: unknown): string {
  const code = (err as { data?: { error?: string } })?.data?.error;
  if (code === "not_author") return "Only the day's author can claim the original.";
  if (code === "original_taken") return "That original is already claimed.";
  if (code === "removed_for_good") return "You removed this one, so it can't come back.";
  if (code === "not_enough") return "A set needs 21 gathered cards.";
  if (code === "in_set") return "That card is in a set: remove the set instead.";
  if (code === "sign_in_required" || (err as { status?: number })?.status === 401) return "Sign in again to pre-order.";
  return "That didn't work. Try again in a bit.";
}
